import {gql} from '@apollo/client';
import {noop} from '@benchling/js-utils';
import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React from 'react';

import {QueryResultType, useBenchlingQuery, useSkippableBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {FolderValueJsonViewerQuery} from 'insights/parameters/__generated/FolderValueJsonViewerQuery';
import {ProjectValueJsonViewerQuery} from 'insights/parameters/__generated/ProjectValueJsonViewerQuery';
import BenchChip from 'ui-library/chips/BenchChip';
import {ChipSize} from 'ui-library/chips/Chip';
import jsonValueToReadable from 'util/jsonValueToReadable';
import {JSONValue} from 'util/Types';

import {InaccessibleInfoGraphQL} from '../../../graphql-fragments/auth/__generated/InaccessibleInfoGraphQL';
import {AnalyticsParameterConfigGraphQL} from '../../../graphql-fragments/insights/__generated/AnalyticsParameterConfigGraphQL';
import {PartiesLinkValueJsonViewerQuery} from './__generated/PartiesLinkValueJsonViewerQuery';
import {SelectorOptionsJsonViewerQuery} from './__generated/SelectorOptionsJsonViewerQuery';
import getParameterType from './data/getParameterType';
import {VALUE_VALIDATORS} from './data/ValueValidators';
import {PARAMETER_TYPE_TO_READABLE} from './ParameterConstants';
import {EDIT_VALUE_TOOLTIP_TITLE, ParameterPillPartTooltipWrapper} from './ParameterPill';
import {ParameterType} from './ParameterTypes';

type TeamNode = ExtractByTypename<PartiesLinkValueJsonViewerQuery['parties'][number], 'TeamNode'>;

type UserNode = ExtractByTypename<PartiesLinkValueJsonViewerQuery['parties'][number], 'UserNode'>;

type AnalyticsParameterSelectorConfig = ExtractByTypename<
  AnalyticsParameterConfigGraphQL,
  'AnalyticsParameterSelectorConfigNode'
>;

type SchemaFieldSelectorOption = ExtractByTypename<
  SelectorOptionsJsonViewerQuery['schemaFieldSelector'],
  'SchemaFieldSelectorNode'
>['schemaFieldSelectorOptions'][number];

enum ValueViewType {
  PLAIN_TEXT = 'PLAIN_TEXT',
  CHIP = 'CHIP',
  FOLDER = 'FOLDER',
  SELECTOR = 'SELECTOR',
  PROJECT = 'PROJECT',
  PARTY = 'PARTY',
}

const MAX_VALUES_TO_SHOW = 3;

const PARAMETER_TYPE_TO_VALUE_VIEW_TYPES: {
  [k in ParameterType]: ValueViewType;
} = {
  [ParameterType.BIOENTITY_LINK]: ValueViewType.CHIP,
  [ParameterType.DATE]: ValueViewType.PLAIN_TEXT,
  [ParameterType.ENTRY_LINK]: ValueViewType.CHIP,
  [ParameterType.FOLDER_LINK]: ValueViewType.FOLDER,
  [ParameterType.NUMBER]: ValueViewType.PLAIN_TEXT,
  [ParameterType.PROCESS_LINK]: ValueViewType.CHIP,
  [ParameterType.PROCESS_TASK_LINK]: ValueViewType.CHIP,
  [ParameterType.PROJECT_LINK]: ValueViewType.PROJECT,
  [ParameterType.SELECTOR]: ValueViewType.SELECTOR,
  [ParameterType.STORABLE_LINK]: ValueViewType.CHIP,
  [ParameterType.STRING]: ValueViewType.PLAIN_TEXT,
  [ParameterType.STUDY_LINK]: ValueViewType.CHIP,
  [ParameterType.TEAM_LINK]: ValueViewType.PARTY,
  [ParameterType.USER_LINK]: ValueViewType.PARTY,
};

function OverflowValuesText({numValues}: {numValues: number}): JSX.Element {
  return (
    <span className="u-primary-color u-tiny-spacing-left u-vertical-center-items">+{numValues} more</span>
  );
}

export function MultiTextValueJsonViewer<T>(props: {
  items: Array<T | InaccessibleInfoGraphQL>;
  generateText: (item: T) => string;
  isAccessible?: (item: T) => boolean;
}): JSX.Element {
  const {generateText, items, isAccessible} = props;
  const overflowItems = items.slice(MAX_VALUES_TO_SHOW);

  return (
    <>
      {items.slice(0, MAX_VALUES_TO_SHOW).map((item, index) => {
        const textComponent =
          // @ts-expect-error: TODO(BNCH-65653). `item` can be null, for example in PartiesValueJsonViewer.
          // isInaccessible expects non-null data.
          // Revealed by Typescript 4.8 upgrade.
          isInaccessible(item) || (isAccessible != null && !isAccessible(item)) ? (
            <InaccessibleValueJsonViewer />
          ) : (
            <TextValueJsonViewer valueJson={generateText(item)} />
          );

        return (
          <span className={css(styles.flexNoWrapWrapper)} key={index}>
            {textComponent}
            {index < items.length - 1 ? <>,&nbsp;</> : null}
          </span>
        );
      })}
      {overflowItems.length > 0 && <OverflowValuesText numValues={overflowItems.length} />}
    </>
  );
}

function TextValueJsonViewer({valueJson}: {valueJson: JSONValue; className?: string | null}): JSX.Element {
  return (
    <div className={classNames(css(styles.textViewer), 'u-primary-color')}>
      {jsonValueToReadable(valueJson)}
    </div>
  );
}

function LoadingValueJsonViewer(): JSX.Element {
  return <div className={classNames(css(styles.textViewer), 'faded')}>Loading...</div>;
}

function InaccessibleValueJsonViewer(): JSX.Element {
  return <div className={classNames(css(styles.textViewer), 'u-danger-color')}>Inaccessible</div>;
}

function MultiChipValueJsonViewer({
  ids,
  tooltipTitleOverride,
}: {
  ids: Array<string>;
  tooltipTitleOverride?: string;
}): JSX.Element {
  const overflowItems = ids.slice(MAX_VALUES_TO_SHOW);

  return (
    <>
      {ids.slice(0, MAX_VALUES_TO_SHOW).map((id, index) => (
        <ChipValueJsonViewer
          id={id}
          tooltipTitleOverride={tooltipTitleOverride}
          key={index}
          className={index > 0 ? 'u-tiny-spacing-left' : undefined}
        />
      ))}
      {overflowItems.length > 0 && <OverflowValuesText numValues={overflowItems.length} />}
    </>
  );
}

function ChipValueJsonViewer({
  id,
  tooltipTitleOverride,
  className,
}: {
  id: string;
  tooltipTitleOverride?: string;
  className?: string;
}): JSX.Element {
  return (
    <BenchChip
      id={id}
      className={classNames(className != null ? className : '', css(styles.pillChip))}
      size={ChipSize.TABLE_CELL}
      onClick={noop}
      tooltipTitleOverride={tooltipTitleOverride}
    />
  );
}

export const SELECTOR_OPTIONS_JSON_VIEWER_QUERY = gql`
  query SelectorOptionsJsonViewerQuery($id: StrictString!) @skipAudit @engComponent(name: INSIGHTS) {
    schemaFieldSelector(id: $id) {
      ... on SchemaFieldSelectorNode {
        id
        schemaFieldSelectorOptions {
          ...SchemaFieldSelectorOptionGraphQL
        }
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('SchemaFieldSelectorOption').fragment}
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

function SelectorValueJsonViewer({
  valueJson,
  schemaFieldSelectorId,
}: {
  valueJson: Array<string> | string;
  schemaFieldSelectorId: string | null;
}): JSX.Element | null {
  const result = useSkippableBenchlingQuery<SelectorOptionsJsonViewerQuery>(
    SELECTOR_OPTIONS_JSON_VIEWER_QUERY,
    {
      variables: {id: schemaFieldSelectorId ?? ''},
      skip: schemaFieldSelectorId == null,
    }
  );
  if (result.type === QueryResultType.ERROR || result.type === QueryResultType.SKIPPED) {
    return null;
  } else if (result.type === QueryResultType.PENDING) {
    return <LoadingValueJsonViewer />;
  } else {
    const schemaFieldSelector = result.data.schemaFieldSelector;

    if (isInaccessible(schemaFieldSelector)) {
      return <InaccessibleValueJsonViewer />;
    }

    const selectedIds = new Set(Array.isArray(valueJson) ? valueJson : [valueJson]);
    const selectedOptions = schemaFieldSelector.schemaFieldSelectorOptions.filter((option) =>
      selectedIds.has(option.id)
    );

    return (
      <>
        <MultiTextValueJsonViewer<SchemaFieldSelectorOption>
          items={selectedOptions}
          generateText={(item: SchemaFieldSelectorOption) => item.name}
        />
      </>
    );
  }
}

export const FOLDER_VALUE_JSON_VIEWER_QUERY = gql`
  query FolderValueJsonViewerQuery($folderIds: [StrictString]!) @skipAudit @engComponent(name: INSIGHTS) {
    folders(ids: $folderIds) {
      ... on FolderNode {
        id
        name
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

function FolderValueJsonViewer({folderId}: {folderId: string}): JSX.Element | null {
  const result = useBenchlingQuery<FolderValueJsonViewerQuery>(FOLDER_VALUE_JSON_VIEWER_QUERY, {
    variables: {folderIds: [folderId]},
  });
  if (result.type === QueryResultType.ERROR) {
    return null;
  } else if (result.type === QueryResultType.PENDING) {
    return <LoadingValueJsonViewer />;
  } else if (result.data.folders.length === 0 || isInaccessible(result.data.folders[0])) {
    return <InaccessibleValueJsonViewer />;
  } else {
    return <TextValueJsonViewer valueJson={result.data.folders[0].name} />;
  }
}

export const PROJECT_VALUE_JSON_VIEWER_QUERY = gql`
  query ProjectValueJsonViewerQuery($projectIds: [StrictString!]!) @skipAudit @engComponent(name: INSIGHTS) {
    projects(ids: $projectIds) {
      ... on ProjectNode {
        id
        name
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

function ProjectValueJsonViewer({projectFolderId}: {projectFolderId: string}): JSX.Element | null {
  const result = useBenchlingQuery<ProjectValueJsonViewerQuery>(PROJECT_VALUE_JSON_VIEWER_QUERY, {
    variables: {projectIds: [projectFolderId]},
  });
  if (result.type === QueryResultType.ERROR) {
    return null;
  } else if (result.type === QueryResultType.PENDING) {
    return <LoadingValueJsonViewer />;
  } else if (result.data.projects.length === 0 || isInaccessible(result.data.projects[0])) {
    return <InaccessibleValueJsonViewer />;
  } else {
    return <TextValueJsonViewer valueJson={result.data.projects[0].name} />;
  }
}

export const PARTIES_LINK_VALUE_JSON_VIEWER_QUERY = gql`
  query PartiesLinkValueJsonViewerQuery($ids: [StrictString!]!) @skipAudit @engComponent(name: INSIGHTS) {
    parties(ids: $ids) {
      ... on UserNode {
        id
        name
      }
      ... on TeamNode {
        id
        name
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

function PartiesValueJsonViewer({ids}: {ids: Array<string>}): JSX.Element | null {
  const result = useBenchlingQuery<PartiesLinkValueJsonViewerQuery>(PARTIES_LINK_VALUE_JSON_VIEWER_QUERY, {
    variables: {ids},
  });
  if (result.type === QueryResultType.ERROR) {
    return null;
  } else if (result.type === QueryResultType.PENDING) {
    return <LoadingValueJsonViewer />;
  } else if (result.data.parties.length === 0) {
    // Inaccessible or non-user party
    return <InaccessibleValueJsonViewer />;
  } else {
    const filteredParties = result.data.parties.map<UserNode | TeamNode | null>((party) =>
      party.__typename === 'UserNode' || party.__typename === 'TeamNode' ? party : null
    );

    return (
      <>
        <MultiTextValueJsonViewer<UserNode | TeamNode | null>
          items={filteredParties}
          generateText={(party) => (party != null ? party.name : '')}
          isAccessible={(party) => party != null}
        />
      </>
    );
  }
}

export interface ValueJsonViewerProps {
  config: AnalyticsParameterConfigGraphQL;
  areQueriesRunning?: boolean;
  // Disables the tooltip on the value. For chip values, the chip's original tooltip with the entity id will
  // still show up, but the insights specific tool will not be shown.
  disableTooltip?: boolean;
  valueJson: JSONValue;
}

export default function ValueJsonViewer({
  config,
  areQueriesRunning = false,
  disableTooltip = false,
  valueJson,
}: ValueJsonViewerProps): JSX.Element {
  const parameterType = getParameterType(config);
  const isValid = VALUE_VALIDATORS[parameterType](valueJson);
  const tooltipTitle = areQueriesRunning
    ? "Parameter values can't be changed while queries are running"
    : isValid
      ? EDIT_VALUE_TOOLTIP_TITLE
      : `Expecting ${PARAMETER_TYPE_TO_READABLE[parameterType]} value. Click to edit value.`;
  let tooltipIsDisabled = disableTooltip;
  let renderedValue;

  if (valueJson == null || (Array.isArray(valueJson) && valueJson.length === 0)) {
    renderedValue = <div className={classNames(css(styles.textViewer), 'faded')}>no value</div>;
  } else if (!isValid) {
    renderedValue = (
      <div className={classNames(css(styles.textViewer), 'u-danger-color')}>
        {jsonValueToReadable(valueJson)}
      </div>
    );
  } else if (PARAMETER_TYPE_TO_VALUE_VIEW_TYPES[parameterType] === ValueViewType.CHIP) {
    const values = Array.isArray(valueJson) ? valueJson : [valueJson];

    renderedValue = (
      <MultiChipValueJsonViewer
        ids={values as Array<string>}
        tooltipTitleOverride={disableTooltip ? undefined : tooltipTitle}
      />
    );
    // Chips render their own tooltips, so this component doesn't need to show one
    tooltipIsDisabled = true;
  } else if (PARAMETER_TYPE_TO_VALUE_VIEW_TYPES[parameterType] === ValueViewType.FOLDER) {
    renderedValue = <FolderValueJsonViewer folderId={valueJson as string} />;
  } else if (PARAMETER_TYPE_TO_VALUE_VIEW_TYPES[parameterType] === ValueViewType.SELECTOR) {
    const selectorConfig = config as AnalyticsParameterSelectorConfig;

    renderedValue = (
      <SelectorValueJsonViewer
        valueJson={valueJson as Array<string> | string}
        schemaFieldSelectorId={selectorConfig.schemaFieldSelector?.id ?? null}
      />
    );
  } else if (PARAMETER_TYPE_TO_VALUE_VIEW_TYPES[parameterType] === ValueViewType.PROJECT) {
    renderedValue = <ProjectValueJsonViewer projectFolderId={valueJson as string} />;
  } else if (PARAMETER_TYPE_TO_VALUE_VIEW_TYPES[parameterType] === ValueViewType.PARTY) {
    const valueJsonAsArray = Array.isArray(valueJson) ? valueJson : [valueJson];
    renderedValue = <PartiesValueJsonViewer ids={valueJsonAsArray as Array<string>} />;
  } else {
    renderedValue = <TextValueJsonViewer valueJson={valueJson} />;
  }

  return (
    <ParameterPillPartTooltipWrapper
      className={css(styles.flexNoWrapWrapper)}
      tooltipProps={{title: tooltipTitle, disabled: tooltipIsDisabled}}
    >
      {renderedValue}
    </ParameterPillPartTooltipWrapper>
  );
}

const styles = StyleSheet.create({
  pillChip: {
    marginRight: '0px',
    maxWidth: '400px',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
  },
  textViewer: {
    // Chip viewers are 20px tall, text viewer is 16px tall so it needs 2px on either side to match the height
    maxWidth: 400,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    padding: '2px 0px',
  },
  flexNoWrapWrapper: {
    display: 'flex',
    flexWrap: 'nowrap',
  },
});
